import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CharacterCard } from './components/CharacterCard';
import { DungeonGrid } from './components/DungeonGrid';
import { EncounterCard } from './components/EncounterCard';
import { LogEntry } from './components/LogEntry';
import { FloatingDice } from './components/FloatingDice';
import { DiceRoller } from './components/DiceRoller';
import Tabs, { Tab } from '@uiw/react-tabs-draggable';
import { getValuesByRegex } from './utils/getLocalStorageValues';
import { ulid } from 'ulid';
import { Character, Encounter, LogEntryType, Position, Grid } from './types';

interface LegacyEncounter {
  name?: string;
  type?: string;
  level?: string | number;
  count?: boolean[] | number;
  attacksPerRound?: string | number;
  status?: string;
  notes?: string;
}

export const FourAgainstDarknessApp: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  if (!slug) throw new Error('Slug parameter is required');

  const savedGrid = localStorage.getItem(`dungeon-${slug}`);
  const savedCharacterPosition = localStorage.getItem(`character-position-${slug}`);
  const savedCharacters = localStorage.getItem(`characters-${slug}`);
  const savedEncounters = ((): string | null => {
    const raw = localStorage.getItem(`encounters-${slug}`);
    if (!raw) return null;
    try {
      const parsed: LegacyEncounter[] = JSON.parse(raw);
      const normalized: Encounter[] = parsed.map((enc, i) => {
        if (typeof enc.count === 'number') {
          return enc as Encounter; // already new format
        }
        // Backward compatibility: convert from legacy format
        return {
          name: enc.name || `Encounter #${i + 1}`,
          type: (enc.type as Encounter['type']) || 'Minion',
          level: parseInt(String(enc.level), 10) || 1,
          count: Array.isArray(enc.count) ? enc.count.filter(Boolean).length + 1 : 0,
          attacksPerRound: parseInt(String(enc.attacksPerRound), 10) || 1,
          status: (enc.status as Encounter['status']) || 'Alive',
          notes: enc.notes || '',
          _new: false,
        };
      });
      return JSON.stringify(normalized);
    } catch (err) {
      console.error('Failed to parse encounters from localStorage', err);
      return null;
    }
  })();
  const savedLogEntries = localStorage.getItem(`log-entries-${slug}`);

  const [characterPosition, setCharacterPosition] = useState<Position | null>(
    savedCharacterPosition ? JSON.parse(savedCharacterPosition) : null,
  );
  const [grid, setGrid] = useState<Grid>(
    savedGrid
      ? JSON.parse(savedGrid)
      : Array(28)
          .fill(null)
          .map(() => Array(20).fill(false)),
  );
  const [characters, setCharacters] = useState<Character[]>(
    savedCharacters
      ? (JSON.parse(savedCharacters) as Character[]).map((char) => ({
          ...char,
          level: +char.level,
          gold: +char.gold,
          attack: +char.attack,
          defense: +char.defense,
          fullLife: +char.fullLife,
          currentLife: +char.currentLife,
        }))
      : Array(4)
          .fill(null)
          .map(() => ({
            name: 'Name',
            class: 'Class',
            level: 1,
            gold: 0,
            attack: 0,
            defense: 0,
            fullLife: 0,
            currentLife: 0,
            equipment: ['bandage'],
            spells: [],
            notes: '',
            id: ulid(),
            key: `characters-${slug}`,
          })),
  );

  const [encounters, setEncounters] = useState<Encounter[]>(
    savedEncounters ? JSON.parse(savedEncounters) : [],
  );

  const [expandedEncounterIndex, setExpandedEncounterIndex] = useState<number | null>(null);

  const [logEntries, setLogEntries] = useState<LogEntryType[]>(
    savedLogEntries ? JSON.parse(savedLogEntries) : [],
  );

  const [newLogEntry, setNewLogEntry] = useState<string>('');

  const [activeCharacterId, setActiveCharacterId] = useState<string>(characters[0].id);

  const [draggingId, setDraggingId] = useState<string | null>(null);

  useEffect(() => {
    if (savedGrid) setGrid(JSON.parse(savedGrid));
    if (savedCharacters) setCharacters(JSON.parse(savedCharacters));
    if (savedEncounters) setEncounters(JSON.parse(savedEncounters));
    if (savedLogEntries) setLogEntries(JSON.parse(savedLogEntries));
    if (savedCharacterPosition) setCharacterPosition(JSON.parse(savedCharacterPosition));
  }, [slug]);

  useEffect(() => {
    localStorage.setItem(`dungeon-${slug}`, JSON.stringify(grid));
  }, [grid, slug]);

  useEffect(() => {
    localStorage.setItem(`characters-${slug}`, JSON.stringify(characters));
  }, [characters, slug]);

  useEffect(() => {
    localStorage.setItem(`encounters-${slug}`, JSON.stringify(encounters));
  }, [encounters, slug]);

  useEffect(() => {
    localStorage.setItem(`log-entries-${slug}`, JSON.stringify(logEntries));
  }, [logEntries, slug]);

  useEffect(() => {
    localStorage.setItem(`character-position-${slug}`, JSON.stringify(characterPosition));
  }, [characterPosition, slug]);

  const addNewEncounter = (): void => {
    const nextNumber = encounters.length + 1;
    const newEncounter: Encounter = {
      name: `Encounter #${nextNumber}`,
      type: 'Minion',
      level: 1,
      count: 1,
      attacksPerRound: 1,
      status: 'Alive',
      notes: '',
    };
    const updated = [...encounters, newEncounter];
    setEncounters(updated);
    setExpandedEncounterIndex(updated.length - 1);
  };

  const addLogEntry = (): void => {
    if (newLogEntry.trim() !== '') {
      const newEntry: LogEntryType = {
        id: Date.now(),
        text: newLogEntry,
        timestamp: new Date().toISOString(),
      };
      setLogEntries((prevEntries) => [newEntry, ...prevEntries]);
      setNewLogEntry('');
    }
  };

  const updateLogEntry = (updatedEntry: LogEntryType): void => {
    setLogEntries((prevEntries) =>
      prevEntries.map((entry) => (entry.id === updatedEntry.id ? updatedEntry : entry)),
    );
  };

  const deleteLogEntry = (id: number): void => {
    setLogEntries((prevEntries) => prevEntries.filter((entry) => entry.id !== id));
  };

  const handleCharacterPosition = (pos: Position): void => {
    setCharacterPosition(pos);
  };

  const handleImport = (key: string, id: string): void => {
    const characters = localStorage.getItem(key);
    if (characters) {
      const parsed: Character[] = JSON.parse(characters).map((c: Character) =>
        c.id === id
          ? {
              name: 'Name',
              class: 'Class',
              level: 1,
              gold: 0,
              attack: 0,
              defense: 0,
              fullLife: 0,
              currentLife: 0,
              equipment: ['bandage'],
              spells: [],
              notes: '',
              id,
              key,
            }
          : c,
      );
      localStorage.setItem(key, JSON.stringify(parsed));
    }
  };

  const navigate = useNavigate();

  const activeCharacterIndex = characters.findIndex((c) => c.id === activeCharacterId);
  const activeCharacter = characters[activeCharacterIndex];
  const importedCharacters = getValuesByRegex(/characters-.*/);
  const filteredCharactersToImport: Character[] = importedCharacters
    .map((charList) => {
      return JSON.parse(charList.value).filter(
        (char: Character) => char.name !== 'Name' && char.id && char.key !== `characters-${slug}`,
      );
    })
    .flat();

  return (
    <>
      <p className="mb-4">
        Bookmark this address to return to your dungeon later:{' '}
        <code>
          /dungeon/<b>{slug}</b>
        </code>
      </p>
      <p className="mb-4">
        You can also save your progress to a file and load it later - even on another browser.
      </p>
      <div className="flex flex-wrap gap-4 mb-4">
        <button
          className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition-colors"
          onClick={() => navigate('/')}
        >
          Home
        </button>
        <button
          className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors"
          onClick={() => {
            const keys = Object.keys(localStorage).filter((key) => key.includes(slug));
            const data: Record<string, string | null> = {};
            keys.forEach((key) => {
              data[key] = localStorage.getItem(key);
            });
            const blob = new Blob([JSON.stringify(data, null, 2)], {
              type: 'application/json',
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `4ad-${slug}-backup.json`;
            link.click();
          }}
        >
          Save Progress
        </button>
      </div>
      <div className="dark:bg-gray-800 bg-gray-100  p-4 space-y-2 rounded">
        <h2 className="text-lg font-bold mb-4 dark:text-white text-gray-800">
          Dungeon Exploration
        </h2>
        <DiceRoller title="Tile (d66)" d="d66" />
        <DiceRoller title="Contents (2d6)" d="2d6" />
        <DiceRoller title="Outcome (d6)" d="d6" />
      </div>
      <DungeonGrid
        grid={grid}
        position={characterPosition}
        encounterCount={encounters.length}
        onGridUpdate={setGrid}
        onCharacterUpdate={handleCharacterPosition}
      />
      <h2 className="text-xl font-bold mt-6 mb-2">Characters</h2>
      <Tabs
        activeKey={activeCharacterId}
        onTabClick={(id) => setActiveCharacterId(id)}
        onTabDrop={(id: string, index?: number) => {
          if (index === undefined) return;

          const oldIndex = characters.findIndex((c) => c.id === id);
          if (oldIndex === -1 || oldIndex === index) return;

          const newCharacters = [...characters];
          const [movedCharacter] = newCharacters.splice(oldIndex, 1);
          newCharacters.splice(index, 0, movedCharacter);

          setCharacters(newCharacters);
        }}
      >
        {characters.map((character) => (
          <Tab
            onDragStart={() => {
              if (draggingId !== character.id) {
                setDraggingId(character.id);
              }
            }}
            onDragEnd={() => {
              setDraggingId(null);
            }}
            key={character.id}
            id={character.id}
            className={`rounded-t px-3 py-2 cursor-pointer ${
              draggingId === character.id ? 'dark:bg-gray-600 bg-gray-50' : ''
            } ${
              activeCharacterId === character.id
                ? 'dark:bg-gray-800 bg-gray-100 font-bold'
                : 'hover:border-gray-300'
            }`}
          >
            <div>{character.name}</div>
            <div className="text-sm dark:text-slate-400 text-gray-500">{character.class}</div>
          </Tab>
        ))}
      </Tabs>
      <CharacterCard
        key={activeCharacterId}
        character={activeCharacter}
        setCharacter={(newCharacter: Character) => {
          const updatedCharacters = [...characters];
          updatedCharacters[activeCharacterIndex] = {
            ...newCharacter,
            id: newCharacter.id ? newCharacter.id : ulid(),
            key: newCharacter.key ? newCharacter.key : `characters-${slug}`,
          };
          setCharacters(updatedCharacters);
        }}
        importedCharacters={filteredCharactersToImport}
        onImport={handleImport}
        setActiveCharacterId={setActiveCharacterId}
      />
      <h2 className="text-xl font-bold mt-6 mb-2">Encounters</h2>
      <div>
        <button
          className="mb-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
          onClick={addNewEncounter}
        >
          + New Encounter
        </button>
      </div>
      <div className="flex flex-col-reverse">
        {encounters.map((encounter, index) => (
          <EncounterCard
            key={index}
            counter={index + 1}
            encounter={encounter}
            isExpanded={expandedEncounterIndex === index}
            onExpand={() => setExpandedEncounterIndex((prev) => (prev === index ? null : index))}
            setEncounter={(newEncounter: Encounter) => {
              const updated = [...encounters];
              updated[index] = newEncounter;
              setEncounters(updated);
            }}
          />
        ))}
      </div>
      <h2 className="text-xl font-bold mt-6 mb-2">Adventure Log</h2>
      <div className="mb-4">
        <textarea
          className="dark:bg-gray-800 dark:text-white shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          rows={3}
          placeholder="Enter a new log entry..."
          value={newLogEntry}
          onChange={(e) => setNewLogEntry(e.target.value)}
        />
        <button
          className="mt-2 bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
          onClick={addLogEntry}
        >
          Add Log Entry
        </button>
      </div>
      <div>
        {logEntries.map((entry) => (
          <LogEntry
            key={entry.id}
            entry={entry}
            updateEntry={updateLogEntry}
            deleteEntry={deleteLogEntry}
          />
        ))}
      </div>
      <FloatingDice />
    </>
  );
};
